import React, { createContext, useContext, useRef } from 'react';
import { CATALOG_DEFAULT_PAGE, CATALOG_MAX_PAGE, CatalogPageSchema, type CatalogPageDto, type ElementNodeDto } from '@xshop/shared';
import { fetchApi } from '../../api/client';
import { listingParams, useListingAddress, type ListingAddress } from './listingAddress';
import { useStoreRead, type StoreRead } from './useStoreRead';

// The one read behind a collection or search page: the products its address asks for, their count, and
// what its filters offer. The grid, the filters, the toolbar and the pagination are separate elements a
// merchant places where they like; they all draw this one read, so the page asks the shop once per change
// rather than once per element, and they can never disagree about how many products there are.

/** What a listing element draws from: the read, whether it still shows the last answer while the next one comes, and the choices. */
export type Listing = { read: StoreRead<CatalogPageDto>; stale: boolean; address: ListingAddress; words: string };

const ListingContext = createContext<Listing | null>(null);

// Gives the page's listing to an element, or nothing on a page that has none.
export const useListing = (): Listing | null => useContext(ListingContext);

// Finds how many cards a page of the listing holds: the listing grid's own count, else Sleek's fifteen.
export function listingPageSize(roots: readonly ElementNodeDto[]): number {
  const stack = [...roots];
  for (let node = stack.pop(); node; node = stack.pop()) {
    const limit = Number(node.props.limit);
    if (node.type === 'product-list' && node.props.source === 'listing') return Number.isFinite(limit) ? Math.min(CATALOG_MAX_PAGE, Math.max(1, Math.trunc(limit))) : CATALOG_DEFAULT_PAGE;
    stack.push(...node.children);
  }
  return CATALOG_DEFAULT_PAGE;
}

// Builds the request a page makes: which collection or which words, the shopper's choices, and the page size.
function browsePath(page: string, address: ListingAddress, limit: number): string | null {
  const own = new URLSearchParams(window.location.search);
  const params = listingParams(address, '');
  if (page === 'collection') params.set('collection', window.location.pathname.match(/^\/collections\/([^/]+)/)?.[1] ?? 'all');
  else if (page === 'search' && (own.get('q') ?? '').trim() !== '') params.set('q', (own.get('q') ?? '').trim());
  else return null;
  params.set('limit', String(limit));
  return `/products/browse?${params.toString()}`;
}

// Reads one listing page and refuses an answer that is not the shape the elements draw.
async function readListing(path: string): Promise<CatalogPageDto> {
  return CatalogPageSchema.parse(await fetchApi<unknown>(path));
}

// Holds the listing of a collection or search page for every listing element inside it; other pages get none.
export const ListingProvider: React.FC<{ page: string; pageSize: number; children: React.ReactNode }> = ({ page, pageSize, children }) => {
  const address = useListingAddress();
  const path = browsePath(page, address, pageSize);
  const read = useStoreRead(path, () => readListing(path ?? ''));
  // A new choice keeps the last answer on screen until the next lands, rather than blanking the grid on every tick.
  const last = useRef<CatalogPageDto | null>(null);
  if (read.status === 'ready') last.current = read.data;
  if (path === null) return <>{children}</>;
  const stale = read.status === 'loading' && last.current !== null;
  const words = new URLSearchParams(window.location.search).get('q') ?? '';
  const shown = stale ? { status: 'ready' as const, data: last.current } : read;
  return <ListingContext.Provider value={{ read: shown, stale, address, words }}>{children}</ListingContext.Provider>;
};
